import { useCallback } from 'react';

import { isEmpty } from 'lodash';
import isArray from 'lodash/isArray';
import { useLocation, useNavigate } from 'react-router-dom';

export const useSearchParamFunctions = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const updateValueInURL = useCallback(
    (field, value) => {
      const searchParams = new URLSearchParams(location.search);
      const nextValue = !isEmpty(value)
        ? isArray(value)
          ? value.join(',')
          : value
        : null;
      const currentValue = searchParams.get(field);

      if (currentValue === nextValue) {
        return;
      }

      if (nextValue !== null) {
        searchParams.set(field, nextValue);
      } else {
        searchParams.delete(field);
      }

      navigate(
        {
          pathname: location.pathname,
          search: searchParams.toString(),
        },
        { replace: true },
      );
    },
    [location.search, location.pathname, navigate],
  );

  const updateValuesInURL = useCallback(
    (updates: Record<string, string | null | undefined>) => {
      const searchParams = new URLSearchParams(location.search);
      let hasChanges = false;

      Object.entries(updates).forEach(([field, value]) => {
        const currentValue = searchParams.get(field);
        if (!!value) {
          if (currentValue === value) return;
          searchParams.set(field, value);
          hasChanges = true;
        } else {
          if (currentValue === null) return;
          searchParams.delete(field);
          hasChanges = true;
        }
      });

      if (!hasChanges) {
        return;
      }

      navigate(
        {
          pathname: location.pathname,
          search: searchParams.toString(),
        },
        { replace: true },
      );
    },
    [location.pathname, location.search, navigate],
  );

  //push value to local storage and set url params
  const updateValue = useCallback(
    (field: string, value: any, localStorageKey: string) => {
      if (localStorageKey) {
        localStorage.setItem(localStorageKey, value || '');
      }
      updateValueInURL(field, value);
    },
    [updateValueInURL],
  );

  return {
    updateValueInURL,
    updateValuesInURL,
    updateValue,
  };
};
